import { Component, ElementRef, OnInit, Input, ViewChild } from '@angular/core';
import { CfChartComponent } from '../chart/chart.component';
import { LineChartModel } from '../../models/chart/line-chart.model';
import { LineChartTemplates } from '../../templates/line-chart.template';
import { ChartStylingModel } from '../../models/chart/chart-styling.model';
import { TemplateService } from '../../services/template-service/template.service';
import { CfEventService } from '../../services/event-service/event.service';
/**
 * <p>CfLineChart component is created based on
 * <b><a href="https://github.com/swimlane/ngx-charts" target="_blank">Ngx-charts</a></b> and
 * <b>CfChartComponet</b></p>
 * <code><pre>
 * import {{'{'}} LineChartModel {{'}'}} from 'cedrus-fusion'
 * import {{'{'}} ChartStylingModel {{'}'}} from 'cedrus-fusion'
 * <code><</code>cf-line-chart<code>></code><code><</code><code>/</code>cf-line-chart<code>></code>
 * </pre></code>
 * <code><pre>{{'{'}}
 * 	data: any[], 										// Data to show
 *  view: number[]                  // Dimensions of chart [width, height] by px
 * 	gradient: boolean, 							// Fill elements with a gradient instead of a solid color. Default: false.
 * 	showLegend: boolean, 						// If to show the legend. Default: true.
 * 	colorScheme: ChartsColorScheme, // Array with string css color values. Default: ['#5AA454', '#A10A28', '#C7B42C', '#AAAAAA'].
 * 	showXAxis: boolean, 						// If to show the x axis. Default: true.
 * 	showYAxis: boolean, 						// If to show the y axis. Default: true.
 * 	showXAxisLabel: boolean, 				// If to show the x axis label. Default: true.
 * 	showYAxisLabel: boolean, 				// If to show the y axis label. Default: true.
 * 	xAxisLabel: string, 						// The x axis label
 * 	yAxisLabel: string, 						// The y axis label
 * 	autoScale: boolean,							// Set the minimum value of the y axis to the minimum value in the data, instead of 0. Default: false.
 * 	legendPosition: string,					// Legend position. Can be: top, right, bottom, left. Default: right.
 * 	chartAlignment: string,					// Alignment of chart. Can be: start, center, end. Default: start.
 * {{'}'}}</pre></code>
 * <p>Example of data object:</p>
 * <code><pre>data = [
 *  {{'{'}}
 *    "name": "Germany",
 *    "series": [
 *    	{{'{'}} "name": "2010", "value": 7300000 {{'}'}},
 *    	{{'{'}} "name": "2011", "value": 8940000 {{'}'}}
 *    ]
 *  {{'}'}},
 *  {{'{'}}
 *    "name": "USA",
 *    "series": [
 *    	{{'{'}} "name": "2010", "value": 7870000 {{'}'}},
 *    	{{'{'}} "name": "2011", "value": 8270000 {{'}'}}
 *    ]
 *  {{'}'}},
 *  {{'{'}}
 *    "name": "France",
 *    "series": [
 *    	{{'{'}} "name": "2010", "value": 5000002 {{'}'}},
 *    	{{'{'}} "name": "2011", "value": 5800000 {{'}'}}
 *     ]
 *   {{'}'}}
 * ]</pre></code>
 * <p>Example of chart options:</p>
 * <code><pre>myChartModel = new LineChartModel({{'{'}}
 * 	data: this.data,
 * 	autoScale: false
 * {{'}'}});</pre></code>
 * <code><pre>{{'{'}}
 * 	barsContainerWidth: string, 							// It is css value for chart bars container width. Default: 100%.
 * 	barsContainerHeight: string', 						// It is css value for chart bars container height. Default: 200px.
 * 	container: StylingObject, 	// Styling of chart container
 * {{'}'}}</pre></code>
 * <p>Example of styling object:</p>
 * <code><pre>chartStylingModel = new ChartStylingModel({{'{'}}
 * 	barsContainerWidth: '100%',
 * 	barsContainerHeight: '200px',
 * 	container: {{'{'}}
 * 		class: 'my-chart-class'
 * 	{{'}'}}
 * {{'}'}});</pre></code>
 */
export class CfLineChartComponent extends CfChartComponent implements OnInit {
/**
 * @hidden
 */
cfChartHost;
/**
 * @hidden
 */
cfNgxChart;
/**
 * <p>Set the minimum value of the y axis to the minimum value in the data, instead of 0. Default: false.</p>
 * @param {?} value
 * @return {?}
 */
set autoScale(value: boolean) { this.properties.autoScale = value; }
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 * @param {?} eventService
 */
constructor(public elementRef: ElementRef,
                /**@hidden */ templateService: TemplateService,
public eventService: CfEventService) {
    super(elementRef, templateService, eventService);
    this.properties = new LineChartModel();
  }
/**
 * @hidden
 * @return {?}
 */
ngOnInit(): void {
    super.ngOnInit();
    this.getMyTemplate('line-chart', LineChartTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'line-chart').then( sampler => {
        if (this.properties.colorScheme.domain.length === 0) {
          this.properties.colorScheme = this.colorSchemes.find(s => s.name === this.properties.colorSchemeName);
        }
        this.styling = new ChartStylingModel(Object.assign({}, this.activeTemplate.style, this.styling));
        // console.log('line-chart', this);
      });
    });
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-line-chart',
  template: "<div *ngIf=\"_inited && properties?.display\" [class]=\"styling?.container?.class\" [ngClass]=\"getDynamicClasses(styling?.container?.dynamicClass)\"> <div #cfChartHost [style.height]=\"updateChartHeight(cfNgxChart?.chartElement?.nativeElement?.scrollHeight)\"> <cf-chart [style.width]=\"styling?.barsContainerWidth\" [style.height]=\"styling?.barsContainerHeight\" class=\"{{properties.legendPosition}} {{properties.chartAlignment}}\"> <ng-template #cfComponentChartTemplate> <ngx-charts-line-chart #cfNgxChart class=\"cf-chart-zone\" [view]=\"properties?.view\" [scheme]=\"properties?.colorScheme\" [results]=\"properties?.data\" [gradient]=\"properties?.gradient\" [xAxis]=\"properties?.showXAxis\" [yAxis]=\"properties?.showYAxis\" [legend]=\"properties?.showLegend\" [showXAxisLabel]=\"properties?.showXAxisLabel\" [showYAxisLabel]=\"properties?.showYAxisLabel\" [xAxisLabel]=\"properties?.xAxisLabel\" [yAxisLabel]=\"properties?.yAxisLabel\" [autoScale]=\"properties?.autoScale\" (select)=\"onSelect($event)\"> </ngx-charts-line-chart> </ng-template> </cf-chart> </div> </div> ",
  styles: [""]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
{type: CfEventService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'cfChartHost': [{ type: ViewChild, args: ['cfChartHost', ] },],
'cfNgxChart': [{ type: ViewChild, args: ['cfNgxChart', ] },],
'autoScale': [{ type: Input, args: ['autoScale', ] },],
};
}

function CfLineChartComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfLineChartComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfLineChartComponent.ctorParameters;
/** @type {?} */
CfLineChartComponent.propDecorators;
/**
 * @hidden
 * @type {?}
 */
CfLineChartComponent.prototype.cfChartHost;
/**
 * @hidden
 * @type {?}
 */
CfLineChartComponent.prototype.cfNgxChart;
/** @type {?} */
CfLineChartComponent.prototype.elementRef;
/**
 * @hidden
 * @type {?}
 */
CfLineChartComponent.prototype.eventService;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
